/**
 * Keyset pagination primitives shared across every `list*` service.
 *
 * Every operational list page in the operator app and admin console paginates
 * with a cursor on `(sort_col desc, id desc)`. This file owns:
 *
 *   * The wire shape for cursors and list results.
 *   * `applyKeyset(...)` helper that wires a cursor onto any PostgREST
 *     query builder.
 *   * `selectCount(...)` helper that maps `countMode` -> the second arg of
 *     `.select(...)`.
 *
 * Why keyset (not OFFSET):
 *
 *   * Stable at any depth -- `OFFSET 50000` scans every skipped row even
 *     with a perfect index. Keyset seeks straight to the first row of the
 *     next page in O(log n).
 *   * Composes cleanly with TanStack `useInfiniteQuery`.
 *   * Matches the partial composite indexes installed by
 *     `20260522000000_pagination_keyset_indexes.sql`.
 *
 * Total counts:
 *
 *   * `countMode: 'none'` (default)      -> no count, fastest.
 *   * `countMode: 'estimated'`           -> Postgres planner estimate via
 *                                          `pg_class.reltuples` after RLS,
 *                                          good enough for "Showing X of ~N".
 *   * `countMode: 'exact'`               -> COUNT(*), slow on big tables;
 *                                          surface only when the user
 *                                          explicitly asks for the exact total.
 *
 * The cursor type is intentionally narrow so it serializes cleanly into
 * a TanStack Query cache key: `{ sortValue: string; id: string }` plus a
 * `sortKey` discriminator that names which column we're sorting on, so a
 * page can switch sort order without leaking a stale cursor across keys.
 */

/**
 * Cursor format for keyset pagination. `sortValue` is the value of the
 * primary sort column (typically a timestamp-ish ISO string). `id` is
 * the row's UUID, used as the deterministic tiebreaker when the sort
 * column has duplicates.
 */
export interface KeysetCursor {
  sortValue: string;
  id: string;
}

/**
 * Standard return shape for every paginated `list*` service.
 *
 *   * `rows` -- the slice for this page (length <= pageSize).
 *   * `nextCursor` -- pass back into the next call. `null` means
 *     end-of-list; the UI should stop calling fetchNextPage.
 *   * `total` -- planner estimate or exact count, depending on
 *     `countMode`. `null` when `countMode: 'none'`.
 *   * `totalIsExact` -- whether `total` is exact (`countMode: 'exact'`)
 *     or estimated. UI uses this to render "~N" vs "N".
 */
export interface ListResult<T> {
  rows: T[];
  nextCursor: KeysetCursor | null;
  total: number | null;
  totalIsExact: boolean;
}

export type CountMode = 'none' | 'estimated' | 'exact';

/**
 * Common pagination options accepted by every paginated `list*` service.
 * Module-specific input types extend this so call sites get a single
 * coherent shape.
 */
export interface KeysetInput {
  cursor?: KeysetCursor | null;
  /** Default 50. Service implementations cap at 200 to defend the API. */
  pageSize?: number;
  /** Default 'none'. Use 'estimated' for the "Showing X of ~N" badge. */
  countMode?: CountMode;
}

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

export function clampPageSize(n: number | undefined): number {
  if (!n || n <= 0) return DEFAULT_PAGE_SIZE;
  return Math.min(n, MAX_PAGE_SIZE);
}

/**
 * Translate a `countMode` into the `{ count }` option for PostgREST's
 * `.select(...)` second argument. Returns `undefined` when no count is
 * desired, which is the cheapest path.
 */
export function selectCountOption(
  mode: CountMode | undefined,
): { count: 'estimated' | 'exact' } | undefined {
  if (mode === 'estimated') return { count: 'estimated' };
  if (mode === 'exact') return { count: 'exact' };
  return undefined;
}

/**
 * Minimum surface of a PostgREST query builder we touch. We deliberately
 * type-erase the chain because the postgrest-js generic plumbing changes
 * its concrete builder shape at every step (filter -> filter -> order -> ...)
 * and the helper is only called once per query, in the service file
 * itself, where the surrounding code provides the concrete types.
 */
type AnyBuilder = {
  or(filters: string): AnyBuilder;
  order(column: string, opts: { ascending: boolean }): AnyBuilder;
};

/**
 * Wire a keyset cursor onto a PostgREST query builder.
 *
 *   * No cursor -> `order(sortCol desc, id desc)`.
 *   * Cursor    -> `or(sortCol < cv, and(sortCol = cv, id < cid))` and the
 *                  same order.
 *
 * The OR-clause is the standard "tuple comparison" rewrite for SQL
 * dialects that don't support `(a, b) < (x, y)` directly through their
 * REST API. Postgres itself can serve this via the composite keyset
 * indexes installed by `20260522000000_pagination_keyset_indexes.sql`.
 */
export function applyKeyset<B extends AnyBuilder>(
  q: B,
  cursor: KeysetCursor | null | undefined,
  sortCol: string,
): B {
  if (cursor) {
    const escaped = escapeForOr(cursor.sortValue);
    q = q.or(
      `${sortCol}.lt.${escaped},and(${sortCol}.eq.${escaped},id.lt.${cursor.id})`,
    ) as B;
  }
  q = q.order(sortCol, { ascending: false }) as B;
  q = q.order('id', { ascending: false }) as B;
  return q;
}

/**
 * Build a `nextCursor` from the last row of a page, or null if the page
 * was not full (i.e. we just served the last page).
 */
export function buildNextCursor<T extends Record<string, unknown>>(
  rows: T[],
  pageSize: number,
  sortCol: string,
): KeysetCursor | null {
  if (rows.length < pageSize) return null;
  const last = rows[rows.length - 1];
  if (!last) return null;
  const sortValue = last[sortCol];
  const id = last['id'];
  if (typeof sortValue !== 'string' || typeof id !== 'string') return null;
  return { sortValue, id };
}

/**
 * Internal: PostgREST .or() values are comma- and parenthesis-sensitive.
 * Cursor values today are ISO timestamps or UUIDs which contain none of
 * those, but we still escape defensively in case a future caller uses
 * a different sort column.
 */
function escapeForOr(value: string): string {
  return value.replace(/,/g, '\\,').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}
